import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Filter, Download, Upload, RefreshCw, ArrowUpRight, ArrowDownRight, FileText, MoreHorizontal, Loader2 } from 'lucide-react';
import axios from 'axios';
import { setupAxios } from '../../auth';
import { useAuth } from '../../auth';
import { ACCOUNTS_PERMS } from '../core/permissions';
import { AccountsActionDropdown, DropdownPosition, getDropdownPosition } from './AccountsActionDropdown';
const api = axios.create({ baseURL: process.env.REACT_APP_ACCOUNTS_API_URL || '' });
setupAxios(api);

export default function AccountsSalesQuotes() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('All');
  const [quotes, setQuotes] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);

  // Status Change Modal State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalQuoteId, setStatusModalQuoteId] = useState<number | null>(null);
  const [statusModalType, setStatusModalType] = useState<'Accepted' | 'Declined' | null>(null);
  const [statusRemarks, setStatusRemarks] = useState('');
  const [statusFile, setStatusFile] = useState<File | null>(null);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);

  const filters = ['All', 'Draft', 'Submitted', 'Accepted', 'Declined', 'Expired'];

  const fetchQuotes = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.get('/accounts/quotes');
      const data = response.data?.data || response.data || [];
      setQuotes(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Failed to fetch quotes:', err);
      setError('Failed to load quotes. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  useEffect(() => {
    const closeDropdowns = () => {
      setActiveDropdown(null);
      setDropdownPos(null);
    };
    document.addEventListener('click', closeDropdowns);
    return () => document.removeEventListener('click', closeDropdowns);
  }, []);

  // Compute stats
  const totalQuotes = quotes.length;
  const acceptedQuotes = quotes.filter(q => q.status?.toLowerCase() === 'accepted').length;
  const pendingQuotes = quotes.filter(q => ['draft', 'sent', 'submitted'].includes(q.status?.toLowerCase() || '')).length;
  const safeNumber = (val: any): number => {
    let num = Number(val);
    if (isNaN(num)) {
      const clean = typeof val === 'string' ? val.replace(/[^0-9.-]/g, '') : '';
      num = Number(clean);
      if (isNaN(num)) num = 0;
    }
    return num;
  };

  const totalValue = quotes.reduce((sum, q) => sum + safeNumber(q.total || 0), 0);

  // Format currency
  const formatCurrency = (val: any) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(val));
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const stats = [
    { label: 'Total Quotes', value: totalQuotes.toString(), change: '+0%', trend: 'up', color: 'blue' },
    { label: 'Accepted', value: acceptedQuotes.toString(), change: '+0%', trend: 'up', color: 'emerald' },
    { label: 'Pending', value: pendingQuotes.toString(), change: '0%', trend: 'up', color: 'amber' },
    { label: 'Total Value', value: formatCurrency(totalValue), change: '+0%', trend: 'up', color: 'violet' },
  ];

  const filteredQuotes = quotes.filter(q => {
    if (activeFilter !== 'All') {
      const qStatus = q.status?.toLowerCase() || '';
      const filterLower = activeFilter.toLowerCase();
      if (filterLower === 'submitted') {
        if (qStatus !== 'submitted' && qStatus !== 'sent') return false;
      } else {
        if (qStatus !== filterLower) return false;
      }
    }
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const matchQuote = q.quoteNumber ? q.quoteNumber.toLowerCase().includes(searchLower) : false;
    const matchCustomer = q.customerName ? q.customerName.toLowerCase().includes(searchLower) : false;
    return matchQuote || matchCustomer;
  });

  const handleDownloadCSV = () => {
    if (filteredQuotes.length === 0) {
      alert("No quote data to export.");
      return;
    }
    const headers = ["Sr. No.", "Quote #", "Customer", "Date", "Expiry", "Amount", "Status"];
    const rows = filteredQuotes.map((quote, index) => [
      index + 1,
      quote.quoteNumber || '-',
      quote.customerName || '-',
      formatDate(quote.quoteDate),
      formatDate(quote.expiryDate),
      formatCurrency(Number(quote.total || 0)),
      quote.status || 'Draft'
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `quotes_export_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'accepted') return 'bg-emerald-100 text-emerald-700';
    if (s === 'declined' || s === 'expired') return 'bg-red-100 text-red-700';
    if (s === 'sent' || s === 'submitted') return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  const submitStatusChange = async () => {
    if (!statusModalQuoteId || !statusModalType) return;
    setIsSubmittingStatus(true);
    try {
      let statusFilePath = null;
      if (statusFile) {
        const formData = new FormData();
        formData.append('files', statusFile);
        const uploadRes = await api.post('/uploads/quotes', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const uploadedFiles = uploadRes.data?.files || uploadRes.data?.data?.files || [];
        if (uploadedFiles.length > 0) {
          statusFilePath = uploadedFiles[0].path;
        }
      }

      await api.put(`/accounts/quotes/${statusModalQuoteId}`, {
        status: statusModalType,
        statusRemarks,
        statusFilePath
      });

      setStatusModalOpen(false);
      fetchQuotes();
    } catch (err: any) {
      console.error(err);
      alert('Failed to update status');
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const quote = activeDropdown !== null
    ? filteredQuotes.find((quote) => quote.id === activeDropdown)
    : null;

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">

        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">

          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6 space-y-5" id="main-scroll">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Sales</div>
                <h1 className="text-xl font-bold text-slate-900 leading-tight">Quotes</h1>
                <p className="text-xs text-slate-500">Create and manage sales quotations</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>Data refreshed just now</span>
                  <button onClick={fetchQuotes} className="p-1 rounded hover:bg-slate-100 transition-colors" title="Refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-blue-500' : ''}`} />
                  </button>
                </div>
                {hasPermission(ACCOUNTS_PERMS.QUOTE_CREATE) && (
                  <button onClick={() => navigate('/accounts/sales/quotes/new')} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"><Plus className="w-4 h-4" />New Quote</button>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl border border-red-100 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {stats.map((stat, i) => (
                <div key={i} className="card-hover bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-9 h-9 rounded-xl bg-${stat.color}-50 flex items-center justify-center`}><FileText className={`w-4.5 h-4.5 text-${stat.color}-600`} /></div>
                    <span className={`text-[10px] font-semibold flex items-center gap-0.5 ${stat.trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>{stat.trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{stat.change}</span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                  {filters.map(f => (<button key={f} onClick={() => setActiveFilter(f)} className={`px-3 py-1.5 text-[11px] font-bold rounded-lg whitespace-nowrap transition-all ${activeFilter === f ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>{f}</button>))}
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input type="text" placeholder="Search quotes..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 pr-4 py-2 w-56 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all" /></div>
                  {/* Filter button kept for future use.
                  <button className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"><Filter className="w-4 h-4 text-slate-500" /></button>
                  */}
                  <button
                    onClick={handleDownloadCSV}
                    className="p-2 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <Download className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto pb-24">
                <table className="w-full text-left min-w-175">
                  <thead>
                    <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="px-4 py-3 font-medium text-center text-slate-400">Sr. No.</th>
                      <th className="px-4 py-3 font-medium">Quote #</th>
                      <th className="px-4 py-3 font-medium">Customer</th>
                      <th className="px-4 py-3 font-medium">Date</th>
                      <th className="px-4 py-3 font-medium">Expiry</th>
                      <th className="px-4 py-3 font-medium text-right">Amount</th>
                      <th className="px-4 py-3 font-medium text-center">Status</th>
                      <th className="px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="p-16 text-center">
                          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                          <p className="text-slate-500 font-medium">Loading quotes...</p>
                        </td>
                      </tr>
                    ) : filteredQuotes.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          <div className="p-16 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-linear-to-br from-blue-50 to-blue-100 text-blue-500 rounded-2xl flex items-center justify-center mb-5"><FileText className="w-10 h-10" strokeWidth={1.5} /></div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">{searchQuery ? 'No matching quotes' : 'No quotes found'}</h3>
                            <p className="text-slate-500 text-sm text-center max-w-sm mb-6">{searchQuery ? 'Try adjusting your search or filters to find what you are looking for.' : 'Create your first quotation to start closing deals faster.'}</p>
                            {!searchQuery && (
                              <div className="flex items-center gap-3">
                                {hasPermission(ACCOUNTS_PERMS.QUOTE_CREATE) && (
                                  <button onClick={() => navigate('/accounts/sales/quotes/new')} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all flex items-center gap-2"><Plus className="w-4 h-4" />Create Quote</button>
                                )}
                                <button className="px-5 py-2.5 bg-white border border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl text-sm font-bold transition-all flex items-center gap-2"><Upload className="w-4 h-4" />Import CSV</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredQuotes.map((quote, index) => (
                        <tr key={quote.id} className="hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => hasPermission(ACCOUNTS_PERMS.QUOTE_UPDATE) && navigate(`/accounts/sales/quotes/${quote.id}`)}>
                          <td className="px-4 py-3 text-slate-500 font-medium text-center" onClick={e => e.stopPropagation()}>
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 font-semibold text-blue-600 hover:underline">
                            {quote.quoteNumber || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-medium">
                            {quote.customerName || '-'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(quote.quoteDate)}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {formatDate(quote.expiryDate)}
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-700 text-right">
                            {formatCurrency(Number(quote.total || 0))}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${getStatusBadge(quote.status)}`}>
                              {quote.status?.toLowerCase() === 'sent' || quote.status?.toLowerCase() === 'submitted' ? 'Submitted' : (quote.status || 'Draft')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right relative">
                            <button
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all focus:opacity-100"
                              onClick={e => {
                                e.stopPropagation();
                                if (activeDropdown === quote.id) {
                                  setActiveDropdown(null);
                                  setDropdownPos(null);
                                } else {
                                  setDropdownPos(getDropdownPosition(e.currentTarget as HTMLElement));
                                  setActiveDropdown(quote.id);
                                }
                              }}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </button>
                            
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </main>
      </div>


      {/* Status Change Modal */}
      {statusModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setStatusModalOpen(false)}></div>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative z-10 flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Mark Quote as {statusModalType}</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Remarks / Notes</label>
                <textarea
                  value={statusRemarks}
                  onChange={e => setStatusRemarks(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all resize-none h-24"
                  placeholder="Add any remarks for this status change..."
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Attach File (Optional)</label>
                <div className="relative">
                  <input
                    type="file"
                    onChange={e => {
                      if (e.target.files && e.target.files.length > 0) {
                        setStatusFile(e.target.files[0]);
                      }
                    }}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 border-dashed rounded-xl hover:bg-slate-50 transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600">
                      <Upload className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-700 truncate">
                        {statusFile ? statusFile.name : 'Click to upload or drag and drop'}
                      </div>
                      <div className="text-xs text-slate-500">PDF, JPG, PNG up to 10MB</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setStatusModalOpen(false)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                disabled={isSubmittingStatus}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitStatusChange}
                disabled={isSubmittingStatus}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold shadow-sm transition-all disabled:opacity-50"
              >
                {isSubmittingStatus ? 'Saving...' : 'Save Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {quote && (
        <AccountsActionDropdown open={true} position={dropdownPos}>
                                {hasPermission(ACCOUNTS_PERMS.QUOTE_UPDATE) && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      setDropdownPos(null);
                                      navigate(`/accounts/sales/quotes/${quote.id}`);
                                    }}
                                  >
                                    Edit Quote
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.SALES_ORDER_CREATE) && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-[13px] font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                                    onClick={() => {
                                      setActiveDropdown(null);
                                      setDropdownPos(null);
                                      navigate(`/accounts/sales/sales-orders/new`, { state: { quoteData: quote } });
                                    }}
                                  >
                                    Convert to Sales Order
                                  </button>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.QUOTE_UPDATE) && (
                                  <>
                                    <button
                                      className="w-full text-left px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(null);
                                      setDropdownPos(null);
                                        setStatusModalQuoteId(quote.id);
                                        setStatusModalType('Accepted');
                                        setStatusRemarks('');
                                        setStatusFile(null);
                                        setStatusModalOpen(true);
                                      }}
                                    >
                                      Mark as Accepted
                                    </button>
                                    <button
                                      className="w-full text-left px-4 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActiveDropdown(null);
                                      setDropdownPos(null);
                                        setStatusModalQuoteId(quote.id);
                                        setStatusModalType('Declined');
                                        setStatusRemarks('');
                                        setStatusFile(null);
                                        setStatusModalOpen(true);
                                      }}
                                    >
                                      Mark as Declined
                                    </button>
                                  </>
                                )}
                                {hasPermission(ACCOUNTS_PERMS.QUOTE_DELETE) && (
                                  <button
                                    className="w-full text-left px-4 py-2 text-[13px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                                    onClick={async () => {
                                      setActiveDropdown(null);
                                      setDropdownPos(null);
                                      if (window.confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
                                        try {
                                          await api.delete(`/accounts/quotes/${quote.id}`);
                                          fetchQuotes();
                                        } catch (err: any) {
                                          console.error(err);
                                          alert('Failed to delete quote');
                                        }
                                      }
                                    }}
                                  >
                                    Delete Quote
                                  </button>
                                )}
        </AccountsActionDropdown>
      )}

    </>
  );
}
